import { supabase } from './supabase';
import { format } from 'date-fns';
import { payrollPeriodBounds } from './payroll';

export interface PayrollRun {
  id: string;
  business_id: string;
  run_number: number;
  period_start_date: string;
  period_end_date: string;
  total_amount: number;
  total_resources: number;
  run_by: string | null;
  run_at: string;
  status: 'completed' | 'cancelled' | 'voided';
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface PayrollRunItem {
  id: string;
  payroll_run_id: string;
  resource_id: string;
  resource_name: string;
  resource_email: string | null;
  compensation_type: string;
  total_earnings: number;
  total_hours: number | null;
  timeclock_earnings: number;
  flat_rate_earnings: number;
  commission_earnings: number;
  bonus_earnings: number;
  overtime_minutes: number;
  overtime_earnings: number;
  timeclock_count: number;
  flat_rate_count: number;
  commission_count: number;
  bonus_count: number;
  created_at: string;
}

export interface Paystub {
  id: string;
  payroll_run_id: string;
  resource_id: string;
  business_id: string;
  period_start_date: string;
  period_end_date: string;
  gross_earnings: number;
  timeclock_earnings: number;
  flat_rate_earnings: number;
  commission_earnings: number;
  bonus_earnings: number;
  overtime_minutes: number;
  overtime_earnings: number;
  tax_deductions: number;
  ei_deductions: number;
  cpp_deductions: number;
  other_deductions: number;
  total_deductions: number;
  net_pay: number;
  created_at: string;
  updated_at: string;
}

export interface PayrollRunDetails extends PayrollRun {
  items: PayrollRunItem[];
  paystubs: Paystub[];
}

export interface RunStatus {
  hasBeenRun: boolean;
  isVoided: boolean;
  run?: PayrollRun;
}

export interface ResourcePayrollData {
  resource: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    compensation_type: string;
    avatar_url?: string;
  };
  timeclock: {
    entries: any[];
    totalEarnings: number;
    totalMinutes: number;
    overtimeMinutes: number;
    overtimeEarnings: number;
  };
  bookings: Record<string, any>;
  bonuses: {
    entries: any[];
    totalEarnings: number;
  };
  /** Corrections to shifts that were already paid (positive or negative). */
  adjustments: {
    entries: any[];
    totalEarnings: number;
  };
  totalEarnings: number;
}

export interface PayrollDataForPeriod {
  resources: Record<string, ResourcePayrollData>;
  summary: {
    totalAmount: number;
    totalResources: number;
    totalEntries: number;
    /** Shifts flagged needs_review and therefore held out of this run. */
    heldForReview: number;
  };
}

export interface RunPayrollRequest {
  businessId: string;
  periodStartDate: Date;
  periodEndDate: Date;
  resourceIds: string[]; // IDs of resources to include in this run
  notes?: string;
}

/**
 * Get all payroll runs for a business
 */
export async function getPayrollRuns(businessId: string): Promise<PayrollRun[]> {
  const { data, error } = await supabase
    .from('payroll_runs')
    .select('*')
    .eq('business_id', businessId)
    .order('run_number', { ascending: false });

  if (error) {
    console.error('Error fetching payroll runs:', error);
    throw error;
  }

  return data || [];
}

/**
 * Get details of a specific payroll run including items
 */
export async function getPayrollRunDetails(runId: string): Promise<PayrollRunDetails> {
  const { data: run, error: runError } = await supabase
    .from('payroll_runs')
    .select('*')
    .eq('id', runId)
    .single();

  if (runError) {
    console.error('Error fetching payroll run:', runError);
    throw runError;
  }

  const { data: items, error: itemsError } = await supabase
    .from('payroll_run_items')
    .select('*')
    .eq('payroll_run_id', runId)
    .order('resource_name');

  if (itemsError) {
    console.error('Error fetching payroll run items:', itemsError);
    throw itemsError;
  }

  const { data: paystubs, error: paystubsError } = await supabase
    .from('paystubs')
    .select('*')
    .eq('payroll_run_id', runId);

  if (paystubsError) {
    console.error('Error fetching paystubs:', paystubsError);
    throw paystubsError;
  }

  return {
    ...run,
    items: items || [],
    paystubs: paystubs || []
  };
}

/**
 * Check if a period has already been run
 */
export async function checkPeriodRunStatus(
  businessId: string,
  startDate: Date,
  endDate: Date
): Promise<RunStatus> {
  const startDateStr = format(startDate, 'yyyy-MM-dd');
  const endDateStr = format(endDate, 'yyyy-MM-dd');

  const { data, error } = await supabase
    .from('payroll_runs')
    .select('*')
    .eq('business_id', businessId)
    .eq('period_start_date', startDateStr)
    .eq('period_end_date', endDateStr)
    .in('status', ['completed', 'voided'])
    .order('run_at', { ascending: false });

  if (error) {
    console.error('Error checking period run status:', error);
    throw error;
  }

  const completed = data?.find((run) => run.status === 'completed');
  const voided = data?.find((run) => run.status === 'voided');

  if (completed) {
    return { hasBeenRun: true, isVoided: false, run: completed };
  }
  if (voided) {
    return { hasBeenRun: false, isVoided: true, run: voided };
  }

  return { hasBeenRun: false, isVoided: false };
}

export function groupPayrollEntriesByResource(
  entries: any[],
): Record<string, ResourcePayrollData> {
  return entries.reduce<Record<string, ResourcePayrollData>>((acc, entry: any) => {
    if (!acc[entry.resource_id]) {
      acc[entry.resource_id] = {
        resource: {
          id: entry.resource_id,
          first_name: entry.resource_first_name,
          last_name: entry.resource_last_name,
          email: entry.resource_email,
          compensation_type: entry.compensation_type,
          avatar_url: entry.resource_avatar_url,
        },
        timeclock: {
          entries: [],
          totalEarnings: 0,
          totalMinutes: 0,
          overtimeMinutes: 0,
          overtimeEarnings: 0,
        },
        bookings: {},
        bonuses: {
          entries: [],
          totalEarnings: 0,
        },
        adjustments: {
          entries: [],
          totalEarnings: 0,
        },
        totalEarnings: 0,
      };
    }

    if (entry.entry_type === 'adjustment') {
      acc[entry.resource_id].adjustments.entries.push(entry);
      acc[entry.resource_id].adjustments.totalEarnings += entry.earnings || 0;
    } else if (entry.entry_type === 'timeclock') {
      acc[entry.resource_id].timeclock.entries.push(entry);
      acc[entry.resource_id].timeclock.totalEarnings += entry.earnings || 0;
      acc[entry.resource_id].timeclock.totalMinutes += entry.total_minutes || 0;
      acc[entry.resource_id].timeclock.overtimeMinutes +=
        (entry.overtime_minutes || 0) + (entry.double_time_minutes || 0);
      acc[entry.resource_id].timeclock.overtimeEarnings += entry.overtime_earnings || 0;
    } else if (entry.entry_type === 'bonus' || entry.entry_type === 'tip') {
      acc[entry.resource_id].bonuses.entries.push(entry);
      acc[entry.resource_id].bonuses.totalEarnings += entry.earnings || 0;
    } else {
      const bookingKey = entry.booking_id || 'no-booking';
      if (!acc[entry.resource_id].bookings[bookingKey]) {
        acc[entry.resource_id].bookings[bookingKey] = {
          booking_number: entry.booking_number || 0,
          booking_title: entry.booking_title || '',
          start_date: entry.start_date || entry.created_at,
          end_date: entry.end_date || entry.created_at,
          items: [],
          totalHours: 0,
          totalEarnings: 0,
        };
      }
      acc[entry.resource_id].bookings[bookingKey].items.push(entry);
      acc[entry.resource_id].bookings[bookingKey].totalHours += entry.hours || 0;
      acc[entry.resource_id].bookings[bookingKey].totalEarnings += entry.earnings || 0;
    }

    acc[entry.resource_id].totalEarnings += entry.earnings || 0;
    return acc;
  }, {});
}

/**
 * Get payroll data for a specific period (UNPAID entries only)
 * Reuses the same logic as the payroll page
 */
export async function getPayrollDataForPeriod(
  businessId: string,
  startDate: Date,
  endDate: Date
): Promise<PayrollDataForPeriod> {
  const { getPayrollEntries } = await import('./payroll');

  const startDateStr = format(startDate, 'yyyy-MM-dd');
  const endDateStr = format(endDate, 'yyyy-MM-dd');

  const entries = await getPayrollEntries(businessId, startDateStr, endDateStr);
  // Exclude entries already paid in a previous run so re-running a period can't double-pay them,
  // and hold back shifts the engine flagged for review until an admin confirms them.
  const unpaid = entries.filter((entry) => entry.process_status !== 'PAID' && entry.process_status !== 'VOIDED');
  const heldForReview = unpaid.filter((entry) => entry.entry_type === 'timeclock' && entry.needs_review).length;
  // Open shifts are still in progress, so they stay off this run. Flagged shifts stay off until an admin confirms them.
  const unpaidEntries = unpaid.filter(
    (entry) => !(entry.entry_type === 'timeclock' && (entry.needs_review || !entry.clock_out)),
  );
  const data = groupPayrollEntriesByResource(unpaidEntries);

  const resourcesArray = Object.values(data);
  const totalAmount = resourcesArray.reduce((sum, resource) => sum + resource.totalEarnings, 0);
  const totalResources = resourcesArray.length;

  let totalEntries = 0;
  resourcesArray.forEach((resource) => {
    totalEntries += resource.timeclock.entries.length;
    totalEntries += resource.bonuses.entries.length;
    totalEntries += resource.adjustments.entries.length;
    Object.values(resource.bookings).forEach((booking) => {
      totalEntries += booking.items.length;
    });
  });

  return {
    resources: data,
    summary: {
      totalAmount,
      totalResources,
      totalEntries,
      heldForReview,
    },
  };
}

/** Payroll breakdown for a completed run (PAID entries), e.g. resend to accountant. */
export async function getPayrollDataForRun(
  payrollRunId: string,
): Promise<Record<string, ResourcePayrollData>> {
  const { getPayrollEntriesForRun } = await import('./payroll');
  const entries = await getPayrollEntriesForRun(payrollRunId);
  return groupPayrollEntriesByResource(entries);
}

/**
 * Execute a payroll run.
 * One database transaction locks the unpaid rows, writes the run, and marks them paid.
 */
export async function runPayroll(request: RunPayrollRequest): Promise<PayrollRun> {
  const { businessId, periodStartDate, periodEndDate, resourceIds, notes } = request;
  const startDateStr = format(periodStartDate, 'yyyy-MM-dd');
  const endDateStr = format(periodEndDate, 'yyyy-MM-dd');
  const { utcStart, utcEnd } = payrollPeriodBounds(startDateStr, endDateStr);

  const { data, error } = await supabase.rpc('run_payroll', {
    p_business_id: businessId,
    p_start: utcStart,
    p_end: utcEnd,
    p_period_start: startDateStr,
    p_period_end: endDateStr,
    p_resource_ids: resourceIds,
    p_notes: notes || null,
  });

  if (error) {
    console.error('Error running payroll:', error);
    throw error;
  }

  return data as PayrollRun;
}

/** Stamp this period's unpaid hours voided. Hours already paid in an earlier run stay paid. */
export async function voidPayrollPeriod(request: {
  businessId: string;
  periodStartDate: Date;
  periodEndDate: Date;
}): Promise<PayrollRun> {
  const startDateStr = format(request.periodStartDate, 'yyyy-MM-dd');
  const endDateStr = format(request.periodEndDate, 'yyyy-MM-dd');
  const { utcStart, utcEnd } = payrollPeriodBounds(startDateStr, endDateStr);

  const { data, error } = await supabase.rpc('void_payroll_period', {
    p_business_id: request.businessId,
    p_start: utcStart,
    p_end: utcEnd,
    p_period_start: startDateStr,
    p_period_end: endDateStr,
  });

  if (error) {
    console.error('Error voiding payroll period:', error);
    throw error;
  }

  return data as PayrollRun;
}

/**
 * Get paystub data for a specific resource in a run
 */
export async function getPaystubData(payrollRunId: string, resourceId: string): Promise<Paystub | null> {
  const { data, error } = await supabase
    .from('paystubs')
    .select('*')
    .eq('payroll_run_id', payrollRunId)
    .eq('resource_id', resourceId)
    .single();

  if (error) {
    console.error('Error fetching paystub data:', error);
    return null;
  }

  return data;
}

/**
 * Get all paystubs for a payroll run
 */
export async function getPaystubsForRun(payrollRunId: string): Promise<Paystub[]> {
  const { data, error } = await supabase
    .from('paystubs')
    .select('*')
    .eq('payroll_run_id', payrollRunId)
    .order('resource_id');

  if (error) {
    console.error('Error fetching paystubs:', error);
    throw error;
  }

  return data || [];
}

/**
 * Get all paystubs for a specific resource
 */
export async function getPaystubsForResource(resourceId: string, businessId: string): Promise<Array<Paystub & { run_number: number; period_start_date: string; period_end_date: string; run_date: string }>> {
  const { data, error } = await supabase
    .from('paystubs')
    .select(`
      *,
      payroll_runs(
        run_number,
        period_start_date,
        period_end_date,
        run_at
      )
    `)
    .eq('resource_id', resourceId)
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching resource paystubs:', error);
    throw error;
  }

  // Flatten the structure and sort by run_at
  const flattenedData = (data || []).map((item: any) => ({
    ...item,
    run_number: item.payroll_runs?.run_number || 0,
    period_start_date: item.payroll_runs?.period_start_date || item.period_start_date,
    period_end_date: item.payroll_runs?.period_end_date || item.period_end_date,
    run_date: item.payroll_runs?.run_at || item.created_at
  }));

  // Sort by run_at descending (most recent first)
  return flattenedData.sort((a, b) => 
    new Date(b.run_date).getTime() - new Date(a.run_date).getTime()
  );
}

